import React, { useState, useEffect } from 'react';
import { DollarSign, Lock, Mail, WifiOff } from 'lucide-react';
import { supabase, checkNetworkConnectivity } from '../lib/supabase';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isOnline, setIsOnline] = useState(true);

  // Check network connectivity on component mount and when user attempts actions
  useEffect(() => {
    const checkConnection = async () => {
      const online = await checkNetworkConnectivity();
      setIsOnline(online);
    };
    
    checkConnection();
    
    // Also listen for browser online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check network connectivity before proceeding
    const online = await checkNetworkConnectivity();
    if (!online) {
      setError('Network connection unavailable. Please check your internet connection and try again.');
      setIsOnline(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (isForgotPassword) {
        await handlePasswordReset();
      } else if (isSignUp) {
        await handleSignUp();
      } else {
        await handleLogin();
      }
    } catch (err) {
      console.error('Authentication error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('Invalid email or password. Please try again.');
        }
        throw error;
      }
    } catch (error) {
      console.error('Authentication error:', error);
      
      // Check if it's a network error
      if (error instanceof Error && 
          (error.message.includes('fetch') || 
           error.message.includes('network') ||
           error.message.includes('Failed to fetch'))) {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
      
      throw new Error(
        error instanceof Error 
          ? error.message 
          : 'Failed to sign in. Please try again.'
      );
    }
  };

  const handleSignUp = async () => {
    if (password.length < 6) {
      throw new Error('Password must be at least 6 characters long');
    }

    try {
      const { error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: window.location.origin
        }
      });
      
      if (error) {
        if (error.message.includes('already registered')) {
          throw new Error('This email is already registered. Please try logging in instead.');
        }
        throw error;
      }
    } catch (error) {
      console.error('Sign up error:', error);
      
      // Check if it's a network error
      if (error instanceof Error && 
          (error.message.includes('fetch') || 
           error.message.includes('network') ||
           error.message.includes('Failed to fetch'))) {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
      
      throw new Error(
        error instanceof Error 
          ? error.message 
          : 'Failed to create account. Please try again.'
      );
    }

    setSuccess('Account created successfully! You can now log in.');
    setEmail('');
    setPassword('');
    setIsSignUp(false);
  };

  const handlePasswordReset = async () => {
    if (!email || !email.includes('@')) {
      throw new Error('Please enter a valid email address');
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setResetEmailSent(true);
      setSuccess('Password reset instructions have been sent to your email.');
    } catch (error) {
      console.error('Password reset error:', error);
      
      // Check if it's a network error
      if (error instanceof Error && 
          (error.message.includes('fetch') || 
           error.message.includes('network') ||
           error.message.includes('Failed to fetch'))) {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
      
      throw new Error(
        error instanceof Error 
          ? error.message 
          : 'Failed to send password reset email. Please try again.'
      );
    }
  };

  const renderForgotPasswordForm = () => (
    <div className="mt-8 space-y-6">
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="text-sm text-green-700">{success}</div>
        </div>
      )}
      
      {!isOnline && (
        <div className="rounded-md bg-yellow-50 p-4 flex items-center">
          <WifiOff className="h-5 w-5 text-yellow-400 mr-2" />
          <div className="text-sm text-yellow-700">You appear to be offline. Please check your internet connection.</div>
        </div>
      )}
      
      <div className="rounded-md shadow-sm -space-y-px">
        <div>
          <label htmlFor="email" className="sr-only">Email address</label>
          <div className="relative">
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              placeholder="Email address"
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <Mail className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <button
          type="submit"
          disabled={loading || resetEmailSent || !isOnline}
          className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Sending...' : 'Send Reset Instructions'}
        </button>
        
        <button
          type="button"
          onClick={() => {
            setIsForgotPassword(false);
            setError(null);
            setSuccess(null);
            setResetEmailSent(false);
          }}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Back to Login
        </button>
      </div>
    </div>
  );

  const renderLoginForm = () => (
    <div className="mt-8 space-y-6">
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="text-sm text-green-700">{success}</div>
        </div>
      )}
      
      {!isOnline && (
        <div className="rounded-md bg-yellow-50 p-4 flex items-center">
          <WifiOff className="h-5 w-5 text-yellow-400 mr-2" />
          <div className="text-sm text-yellow-700">You appear to be offline. Please check your internet connection.</div>
        </div>
      )}
      
      <div className="rounded-md shadow-sm -space-y-px">
        <div>
          <label htmlFor="email" className="sr-only">Email address</label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
            placeholder="Email address"
          />
        </div>
        <div>
          <label htmlFor="password" className="sr-only">Password</label>
          <div className="relative">
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              placeholder="Password"
              minLength={6}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <Lock className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <button
          type="submit"
          disabled={loading || !isOnline}
          className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (isSignUp ? 'Creating account...' : 'Signing in...') : (isSignUp ? 'Create Account' : 'Sign in')}
        </button>
        
        <div className="flex justify-between">
          <button
            type="button"
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError(null);
              setSuccess(null);
            }}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
          </button>

          {!isSignUp && (
            <button
              type="button"
              onClick={() => {
                setIsForgotPassword(true);
                setError(null);
                setSuccess(null);
              }}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Forgot password?
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="h-10 w-10 text-blue-600" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Payrolli</h2>
          <p className="mt-2 text-sm text-gray-600">
            {isForgotPassword 
              ? 'Reset your password'
              : isSignUp 
                ? 'Create your account' 
                : 'Sign in to your account'}
          </p>
        </div>
        
        <form onSubmit={handleSubmit}>
          {isForgotPassword ? renderForgotPasswordForm() : renderLoginForm()}
        </form>
      </div>
    </div>
  );
};

export default Login;